import { useMemo } from 'react';
import { MdOutlineComment, MdOutlineError } from 'react-icons/md';
import { HotelIcon } from '@icons';
import { Tooltip } from '@ds';
import { useRouteContext } from '@lp/core/contexts/RouteContext';
import { AccommodationLeg } from '@/api/apis/route';
import { formatDateTimeStringToTimeZone, getLocationAddress } from '@/utils/helpers';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { ResourcesLegInfo } from './ResourcesLegInfo';

type AccommodationSegmentLegProps = {
  leg: AccommodationLeg;
  handleLegInfoClick?: (leg: AccommodationLeg, segmentId: string | null, prevLegId: string | null) => void;
};

export const AccommodationSegmentLeg = ({ leg, handleLegInfoClick }: AccommodationSegmentLegProps) => {
  const { locationById } = useRouteContext();
  const { resolveSubdivisionName } = useCountriesData();
  const currentLocation = leg.location ? locationById.get(leg.location.locationId) : null;
  const locationTimeZone = currentLocation?.location.timezone || 'America/New_York';
  const startTime = formatDateTimeStringToTimeZone(leg.checkInAt, locationTimeZone, 'MMM dd');
  const endTime = formatDateTimeStringToTimeZone(leg.checkOutAt, locationTimeZone, 'MMM dd');

  const isLegValid = useMemo(() => {
    return !!leg.hotelName && !!currentLocation?.location.addressLine && !!leg.rooms;
  }, [leg, currentLocation]);

  return (
    <div className="flex gap-4 items-stretch pr-4">
      <div className="flex gap-1 w-33 -translate-y-3 justify-end shrink-0">
        <div className="flex flex-col items-end">
          <span className="text-s-r text-base-10">{startTime}</span>
          <span className="text-s-r text-base-10">{endTime}</span>
        </div>
      </div>
      <div className="relative flex flex-col shrink-0 justify-center items-center w-5">
        <div
          className="absolute -top-px box-border w-3.5 h-3.5 bg-white border-3 rounded-full"
          style={{ borderColor: '#F6D1AC' }}
        />
      </div>
      <div className="flex flex-col grow -translate-y-1.5">
        <div className="flex gap-2 items-center w-full pb-3 border-b-1 border-base-55">
          <button onClick={() => handleLegInfoClick?.(leg, null, null)} className="flex gap-2 items-center">
            <div className="text-base-5">
              <HotelIcon />
            </div>
            <div className="flex flex-col items-start text-base-10">
              <span className="text-s-m">{leg.hotelName || 'N/A'}</span>
              <span className="text-s-r">
                {getLocationAddress(currentLocation?.location, resolveSubdivisionName) || 'N/A'}
              </span>
            </div>
            {!!leg.comments && (
              <Tooltip showArrow content={leg.comments}>
                <div className="cursor-pointer">
                  <MdOutlineComment size={18} />
                </div>
              </Tooltip>
            )}
          </button>
        </div>
        <div className="flex flex-col gap-1 mt-2 pb-2 w-full border-b-1 border-base-55">
          {!isLegValid && (
            <div className="w-max flex px-1 py-0.5 rounded-[4px] gap-1 bg-base-70">
              <div className="text-danger-30">
                <MdOutlineError size={16} />
              </div>
              <HotelIcon />
              <span className="text-s-r text-base-10">The accommodation data is missing</span>
            </div>
          )}
          <ResourcesLegInfo legType={leg.legType} hasScs={false} resources={leg.resources} />
        </div>
      </div>
    </div>
  );
};
